// Records the whole meeting into one file: every participant's video plus the mixed audio of everyone in the room.
// It is built from the streams the browser already has, so it needs no browser prompt and shows no "Stop sharing" bar.
// When someone is sharing their screen, that screen is drawn large and everyone else runs along the bottom.

// Timer file served from our own domain (public/recorder-timer.js), so the Content Security Policy allows it.
const TICK_WORKER_URL = '/recorder-timer.js';

// MP4 is preferred where the browser can record it (recent Chrome and Edge). Otherwise WebM.
const CANDIDATE_MIME_TYPES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm',
];

export function pickRecordingMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return CANDIDATE_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

// File extension for the chosen recording format.
export function recordingExtension(mimeType) {
  return mimeType && mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
}

function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}

// Draws one video into a box, or the initials when there is no picture yet.
// fit 'cover' crops to fill the box (good for faces). fit 'contain' shows the whole picture (good for screens).
function drawVideoInBox(ctx, entry, x, y, w, h, fit) {
  ctx.fillStyle = '#1e1e1e';
  ctx.fillRect(x, y, w, h);
  const v = entry.video;
  if (v.readyState >= 2 && v.videoWidth) {
    const scale = fit === 'contain'
      ? Math.min(w / v.videoWidth, h / v.videoHeight)
      : Math.max(w / v.videoWidth, h / v.videoHeight);
    const dw = v.videoWidth * scale;
    const dh = v.videoHeight * scale;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.drawImage(v, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    ctx.restore();
  } else {
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${Math.round(Math.min(w, h) * 0.3)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials(entry.name), x + w / 2, y + h / 2);
  }
}

// Name label, bottom-left of a box.
function drawNameLabel(ctx, entry, x, y, w, h) {
  const label = String(entry.name || 'Participant');
  ctx.font = '18px sans-serif';
  const labelW = Math.min(ctx.measureText(label).width + 16, w - 8);
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.fillRect(x + 8, y + h - 36, labelW, 28);
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x + 16, y + h - 22, labelW - 16);
}

/**
 * @param {() => Array<{id: string, name: string, stream: MediaStream, featured?: boolean}>} getTiles
 *        Returns the current participants. Called about once a second, so joins and leaves are picked up.
 *        A tile with featured: true is the person sharing their screen.
 * @param {{width?: number, height?: number, fps?: number, videoTrack?: MediaStreamTrack|null}} options
 *        videoTrack: optional. When given (browser tab capture), that picture is recorded instead of the drawn layout.
 * @returns {{stream: MediaStream, stop: () => void}}
 */
export function createMeetingRecorder(getTiles, { width = 1280, height = 720, fps = 25, videoTrack = null } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  const audioCtx = new AudioCtx();
  const audioDest = audioCtx.createMediaStreamDestination();

  // Browsers may start an AudioContext suspended. Resume it on the first click or tap in the room.
  const resume = () => { audioCtx.resume().catch(() => {}); };
  audioCtx.resume().catch(() => {});
  document.addEventListener('pointerdown', resume);

  const tiles = new Map();        // tile id -> { video, stream, name, featured }
  const audioSources = new Map(); // audio track id -> source node
  let tick = 0;

  function syncTiles() {
    const current = getTiles() || [];
    const seen = new Set();

    current.forEach((t) => {
      seen.add(t.id);
      let entry = tiles.get(t.id);
      if (!entry) {
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.autoplay = true;
        entry = { video, stream: null, name: t.name, featured: false };
        tiles.set(t.id, entry);
      }
      entry.name = t.name;
      entry.featured = !!t.featured;
      if (entry.stream !== t.stream) {
        entry.stream = t.stream;
        entry.video.srcObject = t.stream;
        entry.video.play().catch(() => {});
      }
    });

    tiles.forEach((_, id) => {
      if (!seen.has(id)) {
        tiles.get(id).video.srcObject = null;
        tiles.delete(id);
      }
    });

    // Mix every audio track in the room into the recording.
    const wanted = new Map();
    current.forEach((t) => {
      t.stream.getAudioTracks().forEach((track) => wanted.set(track.id, track));
    });
    wanted.forEach((track, id) => {
      if (!audioSources.has(id)) {
        try {
          const source = audioCtx.createMediaStreamSource(new MediaStream([track]));
          source.connect(audioDest);
          audioSources.set(id, source);
        } catch (e) {
          console.warn('Recording: could not add an audio track', e);
        }
      }
    });
    audioSources.forEach((source, id) => {
      if (!wanted.has(id)) {
        try { source.disconnect(); } catch (e) { /* already gone */ }
        audioSources.delete(id);
      }
    });
  }

  function drawFrame() {
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, width, height);

    const list = [...tiles.values()];
    if (!list.length) return;

    const sharer = list.find((e) => e.featured);
    if (sharer) {
      // Someone is sharing: the screen fills the whole frame, the same as the live Presentation view. The gallery is hidden.
      drawVideoInBox(ctx, sharer, 0, 0, width, height, 'contain');
      drawNameLabel(ctx, sharer, 0, 0, width, height);
      return;
    }

    // Nobody is sharing: an even grid of everyone.
    const n = list.length;
    const cols = Math.ceil(Math.sqrt(n));
    const rows = Math.ceil(n / cols);
    const cellW = width / cols;
    const cellH = height / rows;
    const pad = 4;

    list.forEach((entry, i) => {
      const x = (i % cols) * cellW + pad;
      const y = Math.floor(i / cols) * cellH + pad;
      const w = cellW - pad * 2;
      const h = cellH - pad * 2;
      drawVideoInBox(ctx, entry, x, y, w, h, 'cover');
      drawNameLabel(ctx, entry, x, y, w, h);
    });
  }

  const onTick = () => {
    if (tick % 25 === 0) syncTiles();
    tick += 1;
    if (!videoTrack) drawFrame();
  };

  // Drive the drawing from the worker timer. If the worker cannot start, use a normal timer instead,
  // so the recording still gets frames.
  let worker = null;
  let fallbackTimer = null;
  const startFallbackTimer = () => {
    if (fallbackTimer === null) fallbackTimer = setInterval(onTick, 40);
  };
  try {
    worker = new Worker(TICK_WORKER_URL);
    worker.onmessage = onTick;
    worker.onerror = () => {
      console.warn('Recording: timer worker failed, using fallback timer');
      try { worker.terminate(); } catch (e) { /* ignore */ }
      worker = null;
      startFallbackTimer();
    };
  } catch (e) {
    console.warn('Recording: timer worker unavailable, using fallback timer', e);
    startFallbackTimer();
  }
  syncTiles();

  const out = new MediaStream([
    ...(videoTrack ? [videoTrack] : canvas.captureStream(fps).getVideoTracks()),
    ...audioDest.stream.getAudioTracks(),
  ]);

  return {
    stream: out,
    stop() {
      if (videoTrack) { try { videoTrack.stop(); } catch (e) { /* already stopped */ } }
      if (worker) { try { worker.terminate(); } catch (e) { /* ignore */ } }
      if (fallbackTimer !== null) clearInterval(fallbackTimer);
      document.removeEventListener('pointerdown', resume);
      audioSources.forEach((source) => { try { source.disconnect(); } catch (e) { /* ignore */ } });
      audioSources.clear();
      tiles.forEach((entry) => { entry.video.srcObject = null; });
      tiles.clear();
      audioCtx.close().catch(() => {});
    },
  };
}
